import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaArrowRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import RegistrationModal from './RegistrationModal'; 
import img1 from '../assest/main1.png'; 
import img3 from '../assest/main5.png'; 
import img2 from '../assest/main6.png'; 

// --- SEO SCHEMA MARKUP (Homepage Organization & Website) ---
const homePageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "PIT Institute (Project Infotech)",
      "url": "https://www.projectinfotech.in",
      "logo": "https://www.projectinfotech.in/logo.png", // Update with your actual logo URL
      "description": "Premium IT Training Institute offering Full Stack Development, Data Science, App Development, and Cloud courses with 100% Placement Assistance.",
      "sameAs": [
        "https://www.linkedin.com/company/your-company",
        "https://www.facebook.com/your-company"
      ]
    },
    {
      "@type": "WebSite",
      "url": "https://www.projectinfotech.in",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.projectinfotech.in/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  ]
};

// --- SEARCH DATA ---
const allSearchableItems = [
    { name: "Java Full Stack", href: "/java-full-stack" },
    { name: "Python Full Stack", href: "/python-full-stack" },
    { name: "Mern Full Stack", href: "/mern-full-stack" },
    { name: "ReactJS Development", href: "/react-js" },
    { name: "Spring Boot", href: "/programming/springboot" },
    { name: "Data Structures & Algorithms", href: "/programming/dsa" },
    { name: "AWS Certification", href: "/cloud-devops/aws" },
    { name: "DevOps", href: "/cloud-devops/devops" },
    { name: "Manual Testing", href: "/software-eng/testing" },
    { name: "Core Java", href: "/programming/core-java" },
    { name: "Python Programming", href: "/programming/python" },
    { name: "SQL Database", href: "/database/sql" },
];

const slideData = [
  {
    id: 1,
    heading: (
      <>
        <span className="block text-white">Learn from the future:</span>
        <span className="text-red-400 block">Explore our AI-powered courses.</span> 
      </>
    ),
    paragraph: 'Your goal is to empower your career ambitions with cutting-edge technology and guidance.',
    ctaText: 'Start Your Journey Today',
    image: img1,
    imageAlt: 'Student preparing for Software Engineering and Tech Career',
  },
  {
    id: 2,
    heading: (
      <>
        <span className="block text-white">Master New Skills:</span>
        <span className="text-red-400 block">Get Certified in Web Development.</span> 
      </>
    ),
    paragraph: 'Build dynamic websites and powerful applications with our industry-leading curriculum.',
    ctaText: 'View Web Courses',
    image: img2,
    imageAlt: 'Developer working on Full Stack Web Development laptop',
  },
  {
    id: 3,
    heading: (
      <>
        <span className="block text-white">Career Transformation:</span>
        <span className="text-red-400 block">Launch your Data Science career.</span> 
      </>
    ),
    paragraph: 'Turn data into decisions with expert-led courses.',
    ctaText: 'Explore Data Science',
    image: img3,
    imageAlt: 'Professional analyzing data for Business Analytics and AI',
  },
];

const HeroSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<{name: string, href: string}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slideData.length);
        setIsTransitioning(false);
      }, 600); 
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchTerm.length > 0) {
        const filtered = allSearchableItems.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSuggestions(filtered);
        setShowSuggestions(true);
    } else {
        setShowSuggestions(false);
    }
  }, [searchTerm]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions.length > 0) window.location.href = suggestions[0].href;
  };

  const currentData = slideData[currentSlide];

  return (
    <section className="bg-gray-900 overflow-hidden relative min-h-fit lg:min-h-screen flex flex-col justify-center">
      
      <Helmet>
        <title>PIT Institute | Best IT Training & Placement Courses</title>
        <meta name="description" content="Master cutting-edge tech skills at Project Infotech. Explore top-rated courses in Full Stack Web Development, App Dev, Data Science, and Cloud. 100% Placement assistance." />
        <meta name="keywords" content="IT Training Institute, Java Full Stack, Python Full Stack, MERN Stack, ReactJS, Online Coding Classes, Software Testing, DevOps Training" />
        <script type="application/ld+json">{JSON.stringify(homePageSchema)}</script>
      </Helmet>

      <RegistrationModal isOpen={isRegisterOpen} onClose={() => setIsRegisterOpen(false)} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-28 lg:pt-32 lg:pb-16"> 
        
       <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between relative gap-8 lg:gap-0">
          
          {/* IMAGE AREA - Always slides from RIGHT */}
          <div
            className={`w-full lg:w-1/2 relative flex justify-center order-first lg:order-last
            transition-all duration-700 ease-out transform
            ${isTransitioning ? 'opacity-0 translate-x-20' : 'opacity-100 translate-x-0'}`} 
          >
            <div className="relative w-full max-w-[280px] sm:max-w-[350px] lg:max-w-md z-10">
              <img
                src={currentData.image}
                alt={currentData.imageAlt}
                className="w-full h-auto object-contain drop-shadow-2xl" 
                loading="eager"
              />
            </div>
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-red-600/10 rounded-full blur-[100px] -z-10"></div>
          </div>

          {/* CONTENT AREA - Always slides from LEFT */}
          <div
            className={`w-full lg:w-1/2 text-center lg:text-left transition-all duration-700 ease-out transform
            ${isTransitioning ? 'opacity-0 -translate-x-20' : 'opacity-100 translate-x-0'}`}
          >
            <h1 className="text-2xl sm:text-4xl lg:text-6xl font-extrabold text-white leading-tight mb-4 lg:mb-6 font-aligran">
              {currentData.heading}
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 mb-6 lg:mb-10 font-aligran max-w-xl mx-auto lg:mx-0">
              {currentData.paragraph}
            </p>
            
            <button
              onClick={() => setIsRegisterOpen(true)}
              aria-label="Start your coding journey with PIT"
              className="inline-flex items-center justify-center px-8 py-3 text-base lg:text-lg font-bold text-white bg-blue-900 rounded-full shadow-lg shadow-blue-600/20 transition duration-300 transform hover:bg-blue-800 hover:scale-105 active:scale-95"
            >
              {currentData.ctaText} <FaArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>

      {/* --- FIXED BOTTOM SEARCH BAR --- */}
      <div className="fixed bottom-0 left-0 right-0 z-40 py-3 bg-gray-900/95 backdrop-blur-md border-t border-red-900/40">
        <div className="w-full px-4 sm:container sm:mx-auto" ref={searchRef}>
          <form className="flex gap-2 items-center w-full" onSubmit={handleSearchSubmit}>
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search for Java, Python, React, DevOps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => { if(searchTerm) setShowSuggestions(true); }}
                aria-label="Search tech courses"
                className="w-full py-2.5 px-3 pl-10 border border-red-900/50 bg-gray-800 text-white rounded-xl focus:outline-none focus:ring-1 focus:ring-red-600 text-sm sm:text-base transition-all"
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" aria-hidden="true" />
              {showSuggestions && suggestions.length > 0 && (
                  <ul className="absolute bottom-full left-0 w-full bg-white text-gray-800 rounded-t-xl shadow-2xl mb-1 border border-gray-200 overflow-hidden z-50">
                      {suggestions.map((item, index) => (
                          <li key={index}>
                              <button 
                                type="button"
                                onClick={() => window.location.href = item.href}
                                aria-label={`Go to ${item.name} course`}
                                className="w-full text-left px-4 py-3 hover:bg-red-50 hover:text-red-700 transition-colors border-b border-gray-100 last:border-0 text-sm font-medium"
                              >
                                  {item.name}
                              </button>
                          </li>
                      ))}
                  </ul>
              )}
            </div>
            <button type="submit" aria-label="Submit Search" className="px-5 py-2.5 bg-red-700 hover:bg-red-600 text-white font-bold rounded-xl transition-all active:scale-95 text-sm sm:text-base">
              Search
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;